import { Controller } from "@hotwired/stimulus"

// Drives the automation settings form: the chosen automation type and
// "how members enter" reveal only the fields that apply.
// - Recurring shows the recurrence panel and hides re-entry (a recurring
//   automation can't re-enter, for now); Run-once shows re-entry and hides
//   recurrence.
// - "When an event happens" shows the trigger-event picker.
// - The stop-condition picker reveals the reply-only details (attribution note +
//   Oscar confirm) when "reply" is chosen (SCH-378).
// Works with either selectable cards (radios) or plain selects via the
// type/triggerType getters.
export default class extends Controller {
  static targets = [
    "typeRadio", "typeSelect",
    "triggerTypeRadio", "triggerTypeSelect", "triggerEventSelect",
    "recurrencePanel", "reentryPanel", "eventPanel", "datePanel", "stageConditionPanel", "reentryInput",
    "stopConditionRadio", "replyDetailsPanel",
    "dateSource", "dateOffsetUnit", "dateHelp",
    "waitModeRadio", "waitDurationPanel", "waitEventPanel"
  ]

  connect() {
    this.toggle()
  }

  toggle() {
    const recurring = this.type === "recurring"
    const eventTriggered = this.triggerType === "event"
    const dateTriggered = this.triggerType === "date"

    if (this.hasRecurrencePanelTarget) this.recurrencePanelTarget.classList.toggle("hidden", !recurring)
    if (this.hasReentryPanelTarget) this.reentryPanelTarget.classList.toggle("hidden", recurring)
    if (this.hasEventPanelTarget) this.eventPanelTarget.classList.toggle("hidden", !eventTriggered)
    if (this.hasDatePanelTarget) this.datePanelTarget.classList.toggle("hidden", !dateTriggered)

    // Stage filter shows only for the "Pipeline stage changed" event trigger.
    if (this.hasStageConditionPanelTarget) {
      const stageChanged = eventTriggered && this.triggerEvent === "opportunities.stage_changed"
      this.stageConditionPanelTarget.classList.toggle("hidden", !stageChanged)
    }

    // Reply-only details show only when "stop on reply" is the chosen condition.
    if (this.hasReplyDetailsPanelTarget) {
      this.replyDetailsPanelTarget.classList.toggle("hidden", !this.stopsOnReply)
    }

    // Recurring can't re-enter — clear any stale checkbox so it isn't submitted on.
    if (recurring && this.hasReentryInputTarget) this.reentryInputTarget.checked = false

    // Delay editor: an explicit either/or. Both panels carry wait_duration
    // fields under the same name ("wait N" vs "max wait N"), so the inactive
    // panel's inputs must be DISABLED, not just hidden — hidden inputs still
    // submit and the last one would win.
    if (this.hasWaitDurationPanelTarget && this.hasWaitEventPanelTarget) {
      const eventMode = this.waitMode === "event"
      this.setWaitPanel(this.waitDurationPanelTarget, !eventMode)
      this.setWaitPanel(this.waitEventPanelTarget, eventMode)
    }
  }

  setWaitPanel(panel, active) {
    panel.classList.toggle("hidden", !active)
    panel.querySelectorAll("input, select").forEach((el) => { el.disabled = !active })
  }

  get waitMode() {
    return this.waitModeRadioTargets.find((el) => el.checked)?.value || "duration"
  }

  // When the date-trigger source changes, swap the offset-unit options and the
  // help line to match it: member dates (birthday/anniversary) use
  // days/weeks/months, an appointment uses minutes/hours/days.
  dateSourceChanged() {
    if (!this.hasDatePanelTarget || !this.hasDateSourceTarget) return
    const source = this.dateSourceTarget.value

    if (this.hasDateOffsetUnitTarget) {
      const units = this.dateData("dateUnits")[source] || []
      const current = this.dateOffsetUnitTarget.value
      const keep = units.includes(current) ? current : ""
      this.dateOffsetUnitTarget.innerHTML =
        `<option value="">—</option>` +
        units.map((u) => `<option value="${u}"${u === keep ? " selected" : ""}>${this.titleize(u)}</option>`).join("")
    }

    const help = this.dateData("dateHelp")[source]
    if (this.hasDateHelpTarget && help) this.dateHelpTarget.textContent = help
  }

  dateData(key) {
    const raw = this.datePanelTarget.dataset[key]
    try {
      return raw ? JSON.parse(raw) : {}
    } catch {
      return {}
    }
  }

  titleize(value) {
    return value.charAt(0).toUpperCase() + value.slice(1)
  }

  get stopConditionType() {
    if (!this.hasStopConditionRadioTarget) return null
    return this.stopConditionRadioTargets.find((el) => el.checked)?.value || null
  }

  // Checkbox-aware: any checked stop-condition input with value "reply".
  get stopsOnReply() {
    return this.stopConditionRadioTargets.some((el) => el.checked && el.value === "reply")
  }

  get type() {
    if (this.hasTypeRadioTarget) return this.typeRadioTargets.find((el) => el.checked)?.value || null
    return this.hasTypeSelectTarget ? this.typeSelectTarget.value : null
  }

  get triggerType() {
    if (this.hasTriggerTypeRadioTarget) return this.triggerTypeRadioTargets.find((el) => el.checked)?.value || null
    return this.hasTriggerTypeSelectTarget ? this.triggerTypeSelectTarget.value : null
  }

  get triggerEvent() {
    return this.hasTriggerEventSelectTarget ? this.triggerEventSelectTarget.value : null
  }
}
